import { ahuBoardApi } from './ahuBoardApi';
import { recalcTunnelLength } from 'pages/ahu-board/tunnelLength';
import type {
  IAhuBoardSection,
  IAhuBoardUnit,
  IAhuTunnelLugPlacement,
  IAhuDmountRow,
  IAhuIntWallRow,
  IValidationErrorItem,
} from 'models';

/**
 * Phase 6e1 — the §20 endpoint rows owned by the PANELS / TOOLBAR WINDOWS /
 * POPUPS, injected into the 6a `ahuBoardApi` (one reducerPath, shared tags).
 * Mutations carry their §19.2 cascades in the CALLING surface (the React
 * mutation layer re-fires the documented bus events), not here — this file is
 * pure HTTP + cache writes.
 *
 * Conventions: PATCH sends changed-fields-only bodies built by the form layer
 * (§26.3 rule 1 — `aw_*`/`plane`/`plane_name` never sent); Backbone urlRoot
 * trailing-slash quirks preserved per-endpoint (tunnels/sections/units PATCH
 * have NO trailing slash; POST list urls DO).
 */

/** Generic catalog row (CMS/master lists — label = label || model || spn §7.7b). */
export interface ICatalogRow {
  id: number;
  name?: string | null;
  label?: string | null;
  model?: string | null;
  spn?: string | null;
  value?: string | number | null;
  description?: string | null;
  [key: string]: unknown;
}

/** DRF paginated page (PaginatedSelectView backing — 50/page §7.7b). */
export interface IPaginatedPage {
  count: number;
  next: string | null;
  results: ICatalogRow[];
}

/** Per-item validation response (§22.2). */
export interface IItemValidation {
  id: number;
  tag?: string | null;
  section_id?: number;
  tunnel_id?: number;
  section?: string;
  label?: string | null;
  selected?: boolean;
  validation_error_messages: IValidationErrorItem[];
  [key: string]: unknown;
}

/** Drawing title-block row (§20.8 #69). */
export interface IAhuDrawing {
  id?: number;
  title?: string | null;
  description?: string | null;
  number?: string | null;
  scale?: string | null;
  revision?: string | null;
  drawn_by?: string | null;
  checked_by?: string | null;
  user_note_one?: string | null;
  user_note_two?: string | null;
  user_note_three?: string | null;
  dwg_section_lengths?: boolean;
  section_tags_in_dwg?: boolean;
  include_callout_schedule?: boolean;
  callout_schedule_side?: 'left' | 'right';
  include_callout_details?: boolean;
  [key: string]: unknown;
}

/** Lug placement settings row (§20.7 #66-67). */
export interface ILugSettingRow {
  id: number;
  x?: number | string | null;
  y?: number | string | null;
  model?: number | null;
  base_direction?: number | null;
  [key: string]: unknown;
}

const normalizePage = (res: IPaginatedPage | ICatalogRow[]): IPaginatedPage =>
  Array.isArray(res) ? { count: res.length, next: null, results: res } : res;

export const ahuBoardFormsApi = ahuBoardApi.injectEndpoints({
  endpoints: (builder) => ({
    // =====================================================================
    // AHU unit (§20.2)
    // =====================================================================
    patchAhuUnit: builder.mutation<
      IAhuBoardUnit,
      { id: number; data: Record<string, unknown> }
    >({
      query: ({ id, data }) => ({
        url: `/api/v1.0/ahu/units/${id}`,
        method: 'PATCH',
        body: data,
      }),
      async onQueryStarted({ id, data: body }, { dispatch, queryFulfilled }) {
        // QA batch 4 #9 — legacy Backbone save() sets attributes
        // synchronously (controlled checkboxes/radios must flip instantly):
        // apply the patch body optimistically, then merge the server echo.
        dispatch(
          ahuBoardApi.util.updateQueryData('getAhuUnit', id, (draft) =>
            Object.assign(draft, body)
          )
        );
        try {
          const { data } = await queryFulfilled;
          dispatch(
            ahuBoardApi.util.updateQueryData('getAhuUnit', id, (draft) =>
              Object.assign(draft, data)
            )
          );
        } catch {
          /* legacy: no rollback on failed PATCH (§49.2 error path) */
        }
      },
    }),

    // §20.2 #12 — overall height (tab.ahu `get:overall:height` listener).
    getAhuOverallHeight: builder.query<{ overall_height: string | null }, number>({
      query: (id) => `/api/v1.0/ahu/units/${id}/overall_height`,
    }),

    // §20.2 #14-16 — certifications.
    getAhuCertifications: builder.query<ICatalogRow[], number>({
      query: (ahuId) => `/api/v1.0/ahu/units/${ahuId}/certifications/`,
      providesTags: (_r, _e, ahuId) => [{ type: 'Ahu', id: `certs-${ahuId}` }],
    }),
    putAhuCertification: builder.mutation<
      unknown,
      { ahuId: number; certId: number; name: string }
    >({
      query: ({ ahuId, certId, name }) => ({
        url: `/api/v1.0/ahu/units/${ahuId}/certifications/${certId}`,
        method: 'PUT',
        body: { id: certId, name },
      }),
      invalidatesTags: (_r, _e, { ahuId }) => [{ type: 'Ahu', id: `certs-${ahuId}` }],
    }),
    deleteAhuCertification: builder.mutation<unknown, { ahuId: number; certId: number }>({
      query: ({ ahuId, certId }) => ({
        url: `/api/v1.0/ahu/units/${ahuId}/certifications/${certId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_r, _e, { ahuId }) => [{ type: 'Ahu', id: `certs-${ahuId}` }],
    }),

    // §20.2 #17/#18 — transformer VA options.
    // dev renamed the 120V route/field to 115V (urls.py xfmr_115v_rated_va);
    // visible label stays "Desired Transformer - 120V".
    getRatedVa115: builder.query<ICatalogRow[], number>({
      query: (ahuId) => `/api/v1.0/ahu/xfmr_115v_rated_va/${ahuId}`,
    }),
    getRatedVa24: builder.query<ICatalogRow[], number>({
      query: (ahuId) => `/api/v1.0/ahu/xfmr_24v_rated_va/${ahuId}`,
    }),

    // =====================================================================
    // Sections (§20.4) — patch caller wraps in the abort+merge queue
    // =====================================================================
    patchAhuSection: builder.mutation<
      IAhuBoardSection,
      { id: number; tunnelId: number; data: Record<string, unknown> }
    >({
      query: ({ id, data }) => ({
        url: `/api/v1.0/ahu/sections/${id}`,
        method: 'PATCH',
        body: data,
      }),
      async onQueryStarted({ id, tunnelId, data: body }, { dispatch, getState, queryFulfilled }) {
        // QA batch 4 #9 — optimistic apply (legacy model.save sets attrs
        // synchronously, e.g. the TagStrip auto_adjust checkbox); server echo
        // still merges below. Latest-wins aborts are safe: the newest request
        // applies its body last.
        dispatch(
          ahuBoardApi.util.updateQueryData('getTunnelSectionList', tunnelId, (rows) =>
            rows
              .map((row) => (row.id === id ? { ...row, ...body } : row))
              .sort((a, b) => (a.order_rate ?? 0) - (b.order_rate ?? 0))
          )
        );
        // tunnel.js:91-117 `recalc_length` rides EVERY section write, the
        // optimistic one included — otherwise the canvas redraws new bands
        // inside old cabinets. See tunnelLength.ts for the full why.
        recalcTunnelLength({ dispatch, getState }, tunnelId);
        try {
          const { data } = await queryFulfilled;
          dispatch(
            ahuBoardApi.util.updateQueryData('getTunnelSectionList', tunnelId, (rows) =>
              rows
                .map((row) => (row.id === id ? { ...row, ...data } : row))
                .sort((a, b) => (a.order_rate ?? 0) - (b.order_rate ?? 0))
            )
          );
          recalcTunnelLength({ dispatch, getState }, tunnelId);
        } catch {
          /* aborted (latest-wins queue) or failed — no server-echo write */
        }
      },
    }),

    createAhuSection: builder.mutation<IAhuBoardSection, Record<string, unknown>>({
      query: (body) => ({
        url: '/api/v1.0/ahu/sections/',
        method: 'POST',
        body,
      }),
      async onQueryStarted(body, { dispatch, getState, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          const tunnelId = Number(body.tunnel_id ?? data.tunnel_id);
          if (Number.isFinite(tunnelId)) {
            dispatch(
              ahuBoardApi.util.updateQueryData('getTunnelSectionList', tunnelId, (rows) => {
                // api.js:229-239 — displaced sections shift right client-side
                // (mirrors the server's move_all_right), so a mid-tunnel
                // insert lands in its slot without a refetch.
                const order = Number(body.order_rate ?? data.order_rate);
                if (Number.isFinite(order)) {
                  rows.forEach((r) => {
                    if (r.id !== data.id && (r.order_rate ?? 0) >= order) {
                      r.order_rate = (r.order_rate ?? 0) + 1;
                    }
                  });
                }
                if (!rows.some((r) => r.id === data.id)) rows.push(data);
                rows.sort((a, b) => (a.order_rate ?? 0) - (b.order_rate ?? 0));
              })
            );
            // storage:section.collection:sync → recalc_length (tunnel.js:91-99).
            recalcTunnelLength({ dispatch, getState }, tunnelId);
          }
        } catch {
          /* no-op */
        }
      },
    }),

    deleteAhuSection: builder.mutation<unknown, { id: number; tunnelId: number }>({
      query: ({ id }) => ({ url: `/api/v1.0/ahu/sections/${id}`, method: 'DELETE' }),
      async onQueryStarted({ id, tunnelId }, { dispatch, getState, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            ahuBoardApi.util.updateQueryData('getTunnelSectionList', tunnelId, (rows) =>
              rows.filter((row) => row.id !== id)
            )
          );
          // storage:section.collection:remove → recalc_length (tunnel.js:100-108).
          recalcTunnelLength({ dispatch, getState }, tunnelId);
        } catch {
          /* no-op */
        }
      },
    }),

    // §20.4 #28 — drag-reorder persistence.
    moveAhuSection: builder.mutation<unknown, { id: number; next_to: number }>({
      query: ({ id, next_to }) => ({
        url: `/api/v1.0/ahu/sections/${id}/move`,
        method: 'POST',
        body: { next_to },
      }),
    }),

    // §20.4 #29 — bulk cabinet update (multi-select panel).
    bulkUpdateSections: builder.mutation<unknown, Record<string, unknown>>({
      query: (body) => ({
        url: '/api/v1.0/ahu/sections/bulk_update/',
        method: 'POST',
        body,
      }),
    }),

    // §20.4 #30 — bulk delete (server also deletes emptied tunnels).
    bulkDeleteSections: builder.mutation<
      unknown,
      { section_ids: number[]; tunnel_ids?: number[] }
    >({
      query: (body) => ({
        url: '/api/v1.0/ahu/sections/bulk_delete/',
        method: 'DELETE',
        body,
      }),
    }),

    // =====================================================================
    // Casing / floor (§19.2 model.ahu.casing / model.ahu.floor)
    // =====================================================================
    // The cabinet-row select values (outerliner_id/finish_id and
    // under_insulation_id/underliner_id) live on these rows, NOT on the
    // tunnel payload — legacy binds tunnel.casing / tunnel.floor models
    // (tab.ahu.tunnel.cabinet.js renderSelects → s.getModel().get(field)).
    getAhuCasing: builder.query<Record<string, unknown>, number>({
      query: (id) => `/api/v1.0/ahu/casings/${id}`,
      providesTags: (_r, _e, id) => [{ type: 'Ahu', id: `casing-${id}` }],
    }),
    getAhuFloor: builder.query<Record<string, unknown>, number>({
      query: (id) => `/api/v1.0/ahu/floors/${id}`,
      providesTags: (_r, _e, id) => [{ type: 'Ahu', id: `floor-${id}` }],
    }),
    patchAhuCasing: builder.mutation<
      Record<string, unknown>,
      { id: number; data: Record<string, unknown> }
    >({
      query: ({ id, data }) => ({
        url: `/api/v1.0/ahu/casings/${id}`,
        method: 'PATCH',
        body: data,
      }),
      async onQueryStarted({ id, data: body }, { dispatch, queryFulfilled }) {
        // QA batch 4 #9 — optimistic apply (legacy model.save semantics).
        dispatch(
          ahuBoardFormsApi.util.updateQueryData('getAhuCasing', id, (draft) =>
            Object.assign(draft, body)
          )
        );
        try {
          const { data } = await queryFulfilled;
          dispatch(
            ahuBoardFormsApi.util.updateQueryData('getAhuCasing', id, (draft) =>
              Object.assign(draft, data)
            )
          );
        } catch {
          /* no server-echo write on failure (legacy: no rollback) */
        }
      },
    }),
    patchAhuFloor: builder.mutation<
      Record<string, unknown>,
      { id: number; data: Record<string, unknown> }
    >({
      query: ({ id, data }) => ({
        url: `/api/v1.0/ahu/floors/${id}`,
        method: 'PATCH',
        body: data,
      }),
      async onQueryStarted({ id, data: body }, { dispatch, queryFulfilled }) {
        // QA batch 4 #9 — optimistic apply (legacy model.save semantics).
        dispatch(
          ahuBoardFormsApi.util.updateQueryData('getAhuFloor', id, (draft) =>
            Object.assign(draft, body)
          )
        );
        try {
          const { data } = await queryFulfilled;
          dispatch(
            ahuBoardFormsApi.util.updateQueryData('getAhuFloor', id, (draft) =>
              Object.assign(draft, data)
            )
          );
        } catch {
          /* no server-echo write on failure */
        }
      },
    }),

    // =====================================================================
    // Int-walls (§20.4 #31-36) + dmounts (#38-41)
    // =====================================================================
    createIntWall: builder.mutation<
      IAhuIntWallRow,
      { plane: 'vertical' | 'horizontal'; data: Record<string, unknown> }
    >({
      query: ({ plane, data }) => ({
        url: `/api/v1.0/ahu/sections/${plane}_internal_walls/`,
        method: 'POST',
        body: data,
      }),
    }),
    patchIntWall: builder.mutation<
      IAhuIntWallRow,
      { plane: 'vertical' | 'horizontal'; id: number; data: Record<string, unknown> }
    >({
      query: ({ plane, id, data }) => ({
        url: `/api/v1.0/ahu/sections/${plane}_internal_walls/${id}`,
        method: 'PATCH',
        body: data,
      }),
    }),
    deleteIntWall: builder.mutation<
      unknown,
      { plane: 'vertical' | 'horizontal'; id: number }
    >({
      query: ({ plane, id }) => ({
        url: `/api/v1.0/ahu/sections/${plane}_internal_walls/${id}`,
        method: 'DELETE',
      }),
    }),

    createDmount: builder.mutation<IAhuDmountRow, { section_id: number; dist_from: number }>({
      query: (body) => ({
        url: '/api/v1.0/ahu/sections/dmounts/',
        method: 'POST',
        body,
      }),
    }),
    patchDmount: builder.mutation<
      IAhuDmountRow,
      { id: number; data: Record<string, unknown> }
    >({
      query: ({ id, data }) => ({
        url: `/api/v1.0/ahu/sections/dmounts/${id}`,
        method: 'PATCH',
        body: data,
      }),
    }),
    deleteDmount: builder.mutation<unknown, number>({
      query: (id) => ({ url: `/api/v1.0/ahu/sections/dmounts/${id}`, method: 'DELETE' }),
    }),

    // =====================================================================
    // Components / accessories — board-chrome creates & deletes (§20.5/§20.6)
    // =====================================================================
    // §20.5 #46 / §20.6 #53 — POST ahu/<type plural>/ {section_id[, section_2_id]}.
    createConcreteRow: builder.mutation<
      Record<string, unknown>,
      { plural: string; body: Record<string, unknown> }
    >({
      query: ({ plural, body }) => ({
        url: `/api/v1.0/ahu/${plural}/`,
        method: 'POST',
        body,
      }),
    }),
    // §20.6 #52 — DELETE ahu/accessories/<id> (generic row delete).
    deleteAhuAccessory: builder.mutation<unknown, number>({
      query: (id) => ({ url: `/api/v1.0/ahu/accessories/${id}`, method: 'DELETE' }),
    }),
    // §20.6 #54 PATCH — blank-off / direct accessory PATCH from board chrome.
    patchConcreteRow: builder.mutation<
      Record<string, unknown>,
      { plural: string; id: number; data: Record<string, unknown> }
    >({
      query: ({ plural, id, data }) => ({
        url: `/api/v1.0/ahu/${plural}/${id}`,
        method: 'PATCH',
        body: data,
      }),
    }),
    // §20.6 #59 — PATCH ahu/positions/<id> (resolution pipeline / mounting popup).
    patchAhuPosition: builder.mutation<
      Record<string, unknown>,
      { id: number; data: Record<string, unknown> }
    >({
      query: ({ id, data }) => ({
        url: `/api/v1.0/ahu/positions/${id}`,
        method: 'PATCH',
        body: data,
      }),
    }),

    // §20.5 #48 — fan ESP (accessory-delete refetch chain §7.6).
    getFanEsp: builder.query<Record<string, unknown>, number>({
      query: (fanId) => `/api/v1.0/ahu/fans/${fanId}/esp`,
    }),

    // §20.5 #44 — components-in-ahu summary (fan-exists checks).
    getComponentsInAhu: builder.query<Array<Record<string, unknown>>, number>({
      query: (unitId) => `/api/v1.0/ahu/get_components/?unit_id=${unitId}`,
    }),

    // =====================================================================
    // Lifting lugs (§20.7)
    // =====================================================================
    // §20.7 — POST ahu/lugs/ (LugCollection.createModel, collection/tunnel.lugs.js:10):
    // gives a based tunnel the AHULug row its placement hangs off. The response is
    // the same AHULugSerializer body the placement GET returns.
    createTunnelLug: builder.mutation<IAhuTunnelLugPlacement, { tunnel_id: number }>({
      query: (body) => ({
        url: '/api/v1.0/ahu/lugs/',
        method: 'POST',
        body,
      }),
    }),
    createLugSetting: builder.mutation<ILugSettingRow, { tunnel_id: number }>({
      query: (body) => ({
        url: '/api/v1.0/ahu/lug_settings/',
        method: 'POST',
        body,
      }),
    }),
    patchLugSetting: builder.mutation<
      ILugSettingRow,
      { id: number; data: Record<string, unknown> }
    >({
      query: ({ id, data }) => ({
        url: `/api/v1.0/ahu/lug_settings/${id}/`,
        method: 'PATCH',
        body: data,
      }),
    }),
    deleteLugSetting: builder.mutation<unknown, number>({
      query: (id) => ({ url: `/api/v1.0/ahu/lug_settings/${id}/`, method: 'DELETE' }),
    }),
    // §20.9 #86 — CMS lug placement standards readouts.
    getLugParams: builder.query<Record<string, unknown>, void>({
      query: () => '/api/v1.0/cms/1/v1.0.0/ahu/lifting_lugs/params/',
      keepUnusedDataFor: 24 * 60 * 60,
    }),

    // =====================================================================
    // Drawing window (§20.8)
    // =====================================================================
    getAhuDrawing: builder.query<IAhuDrawing, number>({
      query: (ahuId) => `/api/v1.0/ahu/drawings/${ahuId}`,
      providesTags: (_r, _e, ahuId) => [{ type: 'Ahu', id: `drawing-${ahuId}` }],
    }),
    getAhuDrawingCms: builder.query<Record<string, unknown>, void>({
      query: () => '/api/v1.0/cms/1/v1.0.0/ahu/drawings/',
      keepUnusedDataFor: 24 * 60 * 60,
    }),
    patchAhuDrawing: builder.mutation<
      IAhuDrawing,
      { ahuId: number; data: Record<string, unknown> }
    >({
      query: ({ ahuId, data }) => ({
        url: `/api/v1.0/ahu/drawings/${ahuId}`,
        method: 'PATCH',
        body: data,
      }),
      async onQueryStarted({ ahuId, data: body }, { dispatch, queryFulfilled }) {
        // QA batch 4 #9 — optimistic apply (legacy model.save semantics).
        dispatch(
          ahuBoardFormsApi.util.updateQueryData('getAhuDrawing', ahuId, (draft) =>
            Object.assign(draft, body)
          )
        );
        try {
          const { data } = await queryFulfilled;
          dispatch(
            ahuBoardFormsApi.util.updateQueryData('getAhuDrawing', ahuId, (draft) =>
              Object.assign(draft, data)
            )
          );
        } catch {
          /* no-op */
        }
      },
    }),

    // =====================================================================
    // CMS / master catalogs (§20.9 — read-only selects)
    // =====================================================================
    // Generic paginated CMS list (PaginatedSelectView backing; path is the
    // catalog segment, e.g. 'ahu/casing/metal_sheets').
    getCmsPaginated: builder.query<
      IPaginatedPage,
      { mfgId: number; path: string; page: number; constructionClassId?: number | null }
    >({
      query: ({ mfgId, path, page, constructionClassId }) => {
        const ccid =
          constructionClassId != null ? `&construction_class_id=${constructionClassId}` : '';
        return `/api/v1.0/cms/${mfgId}/v1.0.0/${path}/?page=${page}${ccid}`;
      },
      transformResponse: normalizePage,
    }),
    // Off-page value resolution (PaginatedSelect getValue → collection.getById).
    // Legacy pageinated.collection.js getById hits the LIST url with
    // `?<filters>&id=<id>` (there is no /<id>/ detail route — it 404s) and
    // takes models[0] from the returned array.
    getCmsCatalogItem: builder.query<
      ICatalogRow | null,
      { mfgId: number; path: string; id: number; constructionClassId?: number | null }
    >({
      query: ({ mfgId, path, id, constructionClassId }) => {
        const ccid =
          constructionClassId != null ? `construction_class_id=${constructionClassId}&` : '';
        return `/api/v1.0/cms/${mfgId}/v1.0.0/${path}/?${ccid}id=${id}`;
      },
      transformResponse: (resp: ICatalogRow[] | ICatalogRow) =>
        (Array.isArray(resp) ? (resp[0] ?? null) : (resp ?? null)),
    }),

    // §20.9 #82/#83 — int-wall planes + metal sheets.
    getIntWallPlanes: builder.query<ICatalogRow[], void>({
      query: () => '/api/v1.0/cms/1/v1.0.0/ahu/section_internal_wall/planes/',
      keepUnusedDataFor: 24 * 60 * 60,
    }),
    getIntWallMetalSheets: builder.query<IPaginatedPage, { page: number }>({
      query: ({ page }) =>
        `/api/v1.0/cms/1/v1.0.0/ahu/section_internal_wall/metal_sheets/?page=${page}`,
      transformResponse: normalizePage,
    }),

    // §20.9 #84/#85 — construction-method bounds + settings.
    getFramingSettings: builder.query<Record<string, unknown>, void>({
      query: () => '/api/v1.0/cms/1/v1.0.0/ahu/framing/ahu_framing_settings/',
      keepUnusedDataFor: 24 * 60 * 60,
    }),
    getConstructionMethods: builder.query<ICatalogRow[], number>({
      query: (ccid) =>
        `/api/v1.0/cms/1/v1.0.0/ahu/framing/ahu_construction_method_settings/?construction_class_id=${ccid}`,
      // Endpoint returns a settings OBJECT; the method list is nested under
      // `ahu_construction_method` (legacy tab.ahu.js:218 reads
      // `models[0].attributes.ahu_construction_method`). Without this unwrap
      // `methods.find(...)` crashes the whole board at boot.
      transformResponse: (resp: { ahu_construction_method?: ICatalogRow[] } | ICatalogRow[]) =>
        Array.isArray(resp) ? resp : (resp?.ahu_construction_method ?? []),
    }),

    // §20.9 #87-91 — roof params (URL-keyed forever caches, §21.6).
    getRoofShapes: builder.query<ICatalogRow[], void>({
      query: () => '/api/v1.0/master/v1.0.0/ahu/roof_shapes/',
      keepUnusedDataFor: 24 * 60 * 60,
    }),
    getRoofLayers: builder.query<ICatalogRow[], void>({
      query: () => '/api/v1.0/master/v1.0.0/ahu/roof_layers/',
      keepUnusedDataFor: 24 * 60 * 60,
    }),
    getRoofSeamDirections: builder.query<ICatalogRow[], void>({
      query: () => '/api/v1.0/master/v1.0.0/ahu/roof_seam_directions/',
      keepUnusedDataFor: 24 * 60 * 60,
    }),
    getRoofSlopeOrientations: builder.query<ICatalogRow[], { styleId: number | '' }>({
      query: ({ styleId }) =>
        `/api/v1.0/cms/1/v1.0.0/ahu/roof/params/roof_slope_orientation/?roof_slope_style_id=${styleId}`,
      keepUnusedDataFor: 24 * 60 * 60,
    }),
    getRoofSlopeDirections: builder.query<
      ICatalogRow[],
      { styleId: number | ''; orientationId: number | '' }
    >({
      query: ({ styleId, orientationId }) =>
        `/api/v1.0/cms/1/v1.0.0/ahu/roof/params/roof_slope_direction/?roof_slope_orientation_id=${orientationId}&roof_slope_style_id=${styleId}`,
      keepUnusedDataFor: 24 * 60 * 60,
    }),

    // §20.9 #92 — main voltages.
    getMasterVoltages: builder.query<ICatalogRow[], void>({
      query: () => '/api/v1.0/master/v1.0.0/ahu/voltages/',
      keepUnusedDataFor: 24 * 60 * 60,
    }),

    // §20.9 #71/#72 — insert-strip catalogs.
    getCmsComponentsStrip: builder.query<ICatalogRow[], void>({
      query: () => '/api/v1.0/cms/1/v1.0.0/ahu/components/',
      keepUnusedDataFor: 24 * 60 * 60,
    }),
    getCmsAccessoriesStrip: builder.query<ICatalogRow[], void>({
      query: () => '/api/v1.0/cms/1/v1.0.0/ahu/accessories/',
      keepUnusedDataFor: 24 * 60 * 60,
    }),
    // §20.9 #73 — drain_pan/uvc per-section-type availability.
    // dev DELETED the bare drain_pan `available_sections/` route; it is now
    // scoped to the AHU's construction class:
    // `…/drain_pan/available_sections/construction_class/<ccid>`
    // (urls_drain_pans.py:92 — mirrors accessories_available_sections_handler.js:8).
    // uvc (and any other accessory) keeps the UNCHANGED bare route.
    getAccessoryAvailableSections: builder.query<
      Array<Record<string, unknown>>,
      { name: string; constructionClassId?: number | null }
    >({
      query: ({ name, constructionClassId }) =>
        name === 'drain_pan'
          ? `/api/v1.0/cms/1/v1.0.0/ahu/drain_pan/available_sections/construction_class/${constructionClassId}`
          : `/api/v1.0/cms/1/v1.0.0/ahu/${name}/available_sections/`,
      keepUnusedDataFor: 24 * 60 * 60,
    }),

    // drain_pan Type options for the board form — legacy DrainPanView (form.js:36)
    // GET `…/ahu/drain_pan/type/class/?construction_class_id=<cc>&section_id=<component_id>`.
    // Rows: {id,type_id,type,construction_class_id} where `type` is the type name
    // (CMSAHUDrainPanTypeClassSerializer source='type.name').
    getDrainPanTypeClass: builder.query<
      ICatalogRow[],
      { constructionClassId: number; sectionComponentId: number | null }
    >({
      query: ({ constructionClassId, sectionComponentId }) =>
        `/api/v1.0/cms/1/v1.0.0/ahu/drain_pan/type/class/?construction_class_id=${constructionClassId}&section_id=${sectionComponentId ?? ''}`,
      keepUnusedDataFor: 24 * 60 * 60,
    }),

    // =====================================================================
    // Validation family (§22.1 URL table; tunnels variant already in 6a api)
    // =====================================================================
    getComponentValidation: builder.query<IItemValidation, number>({
      query: (id) => `/api/v1.0/ahu/components/${id}/validation`,
    }),
    getAccessoryValidation: builder.query<IItemValidation, number>({
      query: (id) => `/api/v1.0/ahu/accessories/${id}/validation`,
    }),
    // May return ONE or ARRAY (door partners) — normalize to array (§22.2).
    getAccessoriesValidation: builder.query<IItemValidation[], number>({
      query: (id) => `/api/v1.0/ahu/accessories_validation/${id}`,
      transformResponse: (res: IItemValidation | IItemValidation[]) =>
        Array.isArray(res) ? res : [res],
    }),
    getSectionAccessoriesValidation: builder.query<IItemValidation[], number>({
      query: (sectionId) => `/api/v1.0/ahu/accessories/validation/sections/${sectionId}`,
      transformResponse: (res: IItemValidation | IItemValidation[]) =>
        Array.isArray(res) ? res : [res],
    }),
    getDmountValidation: builder.query<IItemValidation, number>({
      query: (id) => `/api/v1.0/ahu/sections/dmounts/${id}/validation`,
    }),

    // =====================================================================
    // Celery-coupled flows (§20.10)
    // =====================================================================
    // #104 — duplicate section (completion via syncdata action.fetch + polling).
    duplicateSectionCelery: builder.mutation<
      { task_id?: string; section_id?: number },
      { section_id: number; tunnel_id: number; order_rate: number; new_tunnel?: boolean }
    >({
      query: (body) => ({
        url: '/ahu/duplicate/section/',
        method: 'POST',
        body,
      }),
    }),

    // §20.3 #20 — POST ahu/tunnels/ (insert flows; api.tunnel.create).
    createAhuTunnel: builder.mutation<
      Record<string, unknown>,
      { ahu_id: number; index_id: number }
    >({
      query: (body) => ({
        url: '/api/v1.0/ahu/tunnels/',
        method: 'POST',
        body,
      }),
    }),
  }),
  overrideExisting: false,
});

export const {
  usePatchAhuUnitMutation,
  useGetAhuCertificationsQuery,
  usePutAhuCertificationMutation,
  useDeleteAhuCertificationMutation,
  useGetRatedVa115Query,
  useGetRatedVa24Query,
  usePatchAhuSectionMutation,
  useCreateAhuSectionMutation,
  useDeleteAhuSectionMutation,
  useMoveAhuSectionMutation,
  useBulkUpdateSectionsMutation,
  useBulkDeleteSectionsMutation,
  useGetAhuCasingQuery,
  useGetAhuFloorQuery,
  usePatchAhuCasingMutation,
  usePatchAhuFloorMutation,
  useCreateIntWallMutation,
  usePatchIntWallMutation,
  useDeleteIntWallMutation,
  useCreateDmountMutation,
  usePatchDmountMutation,
  useDeleteDmountMutation,
  useCreateConcreteRowMutation,
  useDeleteAhuAccessoryMutation,
  usePatchConcreteRowMutation,
  usePatchAhuPositionMutation,
  useGetComponentsInAhuQuery,
  useCreateTunnelLugMutation,
  useCreateLugSettingMutation,
  usePatchLugSettingMutation,
  useDeleteLugSettingMutation,
  useGetLugParamsQuery,
  useGetAhuDrawingQuery,
  useGetAhuDrawingCmsQuery,
  usePatchAhuDrawingMutation,
  useGetIntWallPlanesQuery,
  useGetFramingSettingsQuery,
  useGetConstructionMethodsQuery,
  useGetRoofShapesQuery,
  useGetRoofLayersQuery,
  useGetRoofSeamDirectionsQuery,
  useGetRoofSlopeOrientationsQuery,
  useGetRoofSlopeDirectionsQuery,
  useGetMasterVoltagesQuery,
  useGetCmsComponentsStripQuery,
  useGetCmsAccessoriesStripQuery,
  useGetDrainPanTypeClassQuery,
  useDuplicateSectionCeleryMutation,
} = ahuBoardFormsApi;
